import React, { useState, useEffect } from "react";
import { api } from "../../../utils/apiService";
import "./MysteryBox.css";

const MysteryBox = () => {
  const [availableBoxes, setAvailableBoxes] = useState([]);
  const [claimedBoxes, setClaimedBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [reward, setReward] = useState(null);
  const [showReward, setShowReward] = useState(false);
  const [error, setError] = useState(null);

  const BASE_URL = import.meta.env.VITE_API_URL;

  useEffect(() => {
    fetchMysteryBoxes();
  }, []);

  const fetchMysteryBoxes = async () => {
    try {
      setLoading(true);
      const response = await api.get("/vip/mystery-boxes");
      const data = response.data;
      if (data.success) {
        setAvailableBoxes(data.availableBoxes);
        setClaimedBoxes(data.claimedThisMonth);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const claimMysteryBox = async (tier) => {
    try {
      setClaiming(tier);
      const response = await api.post("/vip/mystery-boxes/claim", { tier });
      const data = response.data;

      if (data.success) {
        setReward(data.reward);
        setShowReward(true);
        // Refresh the boxes after claiming
        fetchMysteryBoxes();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to claim mystery box");
    } finally {
      setClaiming(null);
    }
  };

  const getTierConfig = (tier) => {
    const configs = {
      "Tier I": {
        name: "Bronze Mystery Box",
        color: "#CD7F32",
        gradient: "linear-gradient(135deg, #CD7F32, #B8860B)",
        icon: "📦",
      },
      "Tier II": {
        name: "Silver Mystery Box",
        color: "#C0C0C0",
        gradient: "linear-gradient(135deg, #C0C0C0, #E5E5E5)",
        icon: "🎁",
      },
      "Tier III": {
        name: "Gold Mystery Box",
        color: "#FFD700",
        gradient: "linear-gradient(135deg, #FFD700, #FFA500)",
        icon: "🏆",
      },
      VIP1: {
        name: "Bronze Mystery Box",
        color: "#CD7F32",
        gradient: "linear-gradient(135deg, #CD7F32, #B8860B)",
        icon: "📦",
      },
      VIP2: {
        name: "Silver Mystery Box",
        color: "#C0C0C0",
        gradient: "linear-gradient(135deg, #C0C0C0, #E5E5E5)",
        icon: "🎁",
      },
      VIP3: {
        name: "Gold Mystery Box",
        color: "#FFD700",
        gradient: "linear-gradient(135deg, #FFD700, #FFA500)",
        icon: "🏆",
      },
    };
    return configs[tier] || configs["Tier I"];
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: "#9CA3AF",
      rare: "#3B82F6",
      epic: "#8B5CF6",
      legendary: "#F59E0B",
    };
    return colors[rarity] || colors.common;
  };

  const formatReward = (reward) => {
    if (reward.type === "xp") {
      return `${reward.amount} XP`;
    }
    return reward.item || "Unknown reward";
  };

  if (loading) {
    return (
      <div className="mystery-box-container">
        <div className="loading-state">
          <div className="cosmic-loader"></div>
          <p>Loading Mystery Boxes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mystery-box-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Access Denied</h3>
          <p>{error}</p>
          <small>VIP membership required to access Mystery Boxes</small>
        </div>
      </div>
    );
  }

  return (
    <div className="mystery-box-container">
      <div className="mystery-box-header">
        <h1>🎁 Mystery Boxes</h1>
        <p>Claim your monthly VIP rewards!</p>
      </div>

      <div className="boxes-grid">
        {availableBoxes.map((box) => {
          const config = getTierConfig(box.tier);
          const isClaimed = claimedBoxes.some(
            (claim) => claim.tier === box.tier,
          );
          const isClaimingThis = claiming === box.tier;

          return (
            <div
              key={box.tier}
              className={`mystery-box-card ${isClaimed ? "claimed" : ""}`}
            >
              <div
                className="box-header"
                style={{ background: config.gradient }}
              >
                <div className="box-icon">{config.icon}</div>
                <h3>{box.name || config.name}</h3>
              </div>

              <div className="box-content">
                <div className="box-tier">
                  <span
                    className="tier-badge"
                    style={{ backgroundColor: config.color }}
                  >
                    {box.tier}
                  </span>
                </div>

                <div className="reward-preview">
                  <h4>Possible Rewards:</h4>
                  <div className="rarity-list">
                    {Object.entries(box.dropChances).map(([rarity, chance]) => (
                      <div key={rarity} className="rarity-item">
                        <span
                          className="rarity-dot"
                          style={{ backgroundColor: getRarityColor(rarity) }}
                        ></span>
                        <span className="rarity-name">{rarity}</span>
                        <span className="rarity-chance">{chance}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="box-footer">
                  {isClaimed ? (
                    <div className="claimed-badge">✅ Claimed this month</div>
                  ) : (
                    <button
                      className={`claim-button ${isClaimingThis ? "claiming" : ""}`}
                      onClick={() => claimMysteryBox(box.tier)}
                      disabled={isClaimingThis}
                    >
                      {isClaimingThis ? (
                        <>
                          <div className="spinner"></div>
                          Opening...
                        </>
                      ) : (
                        "Claim Box"
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {availableBoxes.length === 0 && (
        <div className="no-boxes">
          <div className="no-boxes-icon">📭</div>
          <h3>No boxes available</h3>
          <p>You've claimed all available mystery boxes this month!</p>
          <small>Come back next month for more rewards</small>
        </div>
      )}

      {/* Reward Modal */}
      {showReward && reward && (
        <div
          className="reward-modal-overlay"
          onClick={() => setShowReward(false)}
        >
          <div className="reward-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reward-header">
              <h2>🎉 Congratulations!</h2>
              <button
                className="close-modal"
                onClick={() => setShowReward(false)}
              >
                ×
              </button>
            </div>

            <div className="reward-content">
              <div
                className="reward-rarity-badge"
                style={{ backgroundColor: getRarityColor(reward.rarity) }}
              >
                {reward.rarity.toUpperCase()}
              </div>

              <div className="reward-amount">{formatReward(reward)}</div>

              <div className="reward-type">
                {reward.type === "xp" && "⭐ Experience Points"}
                {reward.type === "item" && "🎁 Special Item"}
              </div>
            </div>

            <button
              className="collect-button"
              onClick={() => setShowReward(false)}
            >
              Collect Reward
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MysteryBox;
